import { useEffect, useRef } from 'react';
import { sessionService } from '../services/SessionService';
import { apiClient } from '../api/ApiClient.js';
import { getEfemLocksEventsUrl } from '../api/sse/index.js';
import { efemLockSseEventSchema, type EfemLockSseEvent } from '@cnapse/shared/schemas.js';
import { useServerSentStream } from './useServerSentStream.js';
import { isRealtimeSseEnabled } from '../config/realtime.js';

export type UseEfemLockEventsOptions = {
  enabled?: boolean;
  onLockEvent?: (event: EfemLockSseEvent) => void;
};

/**
 * Tenant-wide EFEM lock SSE — refresh client lock UI on acquire/release/expiry.
 */
export function useEfemLockEvents({
  enabled = true,
  onLockEvent,
}: UseEfemLockEventsOptions): void {
  const onLockEventRef = useRef(onLockEvent);

  useEffect(() => {
    onLockEventRef.current = onLockEvent;
  }, [onLockEvent]);

  const url =
    isRealtimeSseEnabled() && enabled && sessionService.isAuthenticated()
      ? getEfemLocksEventsUrl()
      : null;

  useServerSentStream({
    url,
    enabled: Boolean(url),
    getAccessToken: () => apiClient.getSseAccessToken(),
    parseEvent: (data) => {
      const parsed = efemLockSseEventSchema.safeParse(JSON.parse(data));
      return parsed.success ? parsed.data : null;
    },
    onEvent: (event) => {
      onLockEventRef.current?.(event);
    },
    onReconnect: () => {
      onLockEventRef.current?.({
        type: 'lock_changed',
        efemClientId: '',
        action: 'released',
        at: new Date().toISOString(),
      });
    },
  });
}
